import { useEffect, useRef, useState } from "react";
import { Link, useParams, useSearchParams } from "react-router-dom";
import { useTranslation } from "react-i18next";

import { MathText } from "../../components/MathText";
import { HtmlPaperPanel } from "./HtmlPaperPanel";
import { PrintoutsPanel } from "./PrintoutsPanel";
import {
  useBlueprint,
  useFinalizePaper,
  useGeneratePaper,
  usePaper,
  useRevisePaper,
  useRollbackPaper,
  useUnlockPaper,
} from "@/api/school";

/** 试卷 — Exam Engine P1 FR-B. Generated from the approved blueprint;
 *  teacher revises by selecting items + an instruction (local regeneration
 *  with version snapshots), then finalizes. */
// The item generator emits a markdown subset in stems (**bold** /
// *italic*); render it instead of showing raw asterisks. Same subset the
// PDF renderer (paper_pdf._esc) supports — keep the two in sync.
/** 存量题目里有一批把选项写进了题干,而 options 又会被单独渲染 →
 *  同一组 A/B/C/D 显示两遍(Jarod 2026-08-27)。出题 agent 已禁止再这么
 *  写;这里剥掉尾部那一行/那一段选项,让老版题目也显示正常。
 *  只在确实有 options 且尾部形如选项清单时才剥,避免误伤正文。 */
function stripInlineOptions(stem: string, options?: { key: string; text: string }[] | null): string {
  if (!options || options.length < 2) return stem;
  const keys = options.map((o) => o.key).join("");
  // 结尾处形如 "A. xx B. yy C. zz D. ww" 的一段(允许换行/全角点)
  const re = new RegExp(
    `\\n\\s*[${keys}][.、．][^\\n]*(?:\\s*[${keys}][.、．][^\\n]*){1,}\\s*$`,
    "u",
  );
  const m = stem.match(re);
  if (!m) return stem;
  // 关键判据:这段必须确实复述了 options 的内容,才是"重复的选项清单"。
  // 否则可能是正文里恰好出现的 "A. 城市 B. 乡村 ..." —— 不能误剥。
  const seg = m[0];
  const echoed = options.filter((o) => o.text && seg.includes(o.text)).length;
  if (echoed < options.length) return stem;
  const stripped = stem.replace(re, "").trimEnd();
  return stripped.trim().length > 0 ? stripped : stem;
}

/** 题干/选项/答案统一走 MathText:`$…$` 出 MathML,`**bold**` 出 <b>
 *  (2026-09-15 浏览器验收:数学卷在这一页原样显示 "$-\\dfrac{1}{2}$")。 */
function renderStemMd(text: string) {
  return <MathText text={text} />;
}

/** 答案键可能是字符串、字符串数组或整个对象 —— 之前直接 JSON.stringify,
 *  老师看到的是 `"B"` 和 `["10；9","10, 9"]`。 */
function formatAnswer(ak: unknown): string {
  const v = (ak as { answer?: unknown; answers?: unknown } | null)?.answer
    ?? (ak as { answers?: unknown } | null)?.answers
    ?? ak;
  if (Array.isArray(v)) return v.map((x) => (typeof x === "string" ? x : JSON.stringify(x))).join("\n");
  if (typeof v === "string" || typeof v === "number") return String(v);
  return JSON.stringify(v);
}

export default function PaperPage() {
  const { t } = useTranslation();
  const { examId } = useParams<{ examId: string }>();
  const bp = useBlueprint(examId);
  const paper = usePaper(examId);
  const generate = useGeneratePaper(examId!);
  const revise = useRevisePaper(examId!);
  const finalize = useFinalizePaper(examId!);
  const unlock = useUnlockPaper(examId!);
  const rollback = useRollbackPaper(examId!);

  const [sel, setSel] = useState<Set<number>>(new Set());
  const [instruction, setInstruction] = useState("");
  const [showAnswers, setShowAnswers] = useState(true);
  const [searchParams, setSearchParams] = useSearchParams();

  const p = paper.data;
  const exam = bp.data?.exam;
  const hasApprovedBp = !!bp.data?.blueprint?.approved_at;
  const editable = p?.status === "draft" || p?.status === "in_review";
  const busy = generate.isPending || revise.isPending;

  // 从蓝图页点"组卷"过来时带 ?generate=1:落地即自动按蓝图生成试卷,
  // 老师无需再手动点"按蓝图生成试卷"(Jarod 反馈)。仅在无试卷时触发一次。
  const autoGenFired = useRef(false);
  useEffect(() => {
    if (
      searchParams.get("generate") === "1" &&
      hasApprovedBp &&
      p?.status === "none" &&
      !busy &&
      !autoGenFired.current
    ) {
      autoGenFired.current = true;
      generate.mutate({ notes: "" });
      const next = new URLSearchParams(searchParams);
      next.delete("generate");
      setSearchParams(next, { replace: true });
    }
  }, [searchParams, hasApprovedBp, p?.status, busy, generate, setSearchParams]);

  function toggle(seq: number) {
    const next = new Set(sel);
    if (next.has(seq)) next.delete(seq);
    else next.add(seq);
    setSel(next);
  }

  if (bp.isLoading || paper.isLoading) return <p className="p-6 text-sm text-slate-400">…</p>;
  if (!exam) return <p className="p-6 text-sm text-rose-500">exam not found</p>;

  return (
    <div className="p-6 max-w-5xl mx-auto">
      <header className="mb-5 flex items-baseline justify-between">
        <div>
          <Link to={`/exams/${examId}/blueprint`} className="text-xs text-sky-600 hover:underline">
            ← {t("paper.backToBlueprint")}
          </Link>
          <h1 className="text-2xl font-semibold text-slate-800 mt-1">
            {t("paper.title")} · {exam.title}
          </h1>
          <p className="text-xs text-slate-500 mt-1">
            {exam.class_name} · {t("blueprint.fullMarks", { total: exam.max_total })}
            {p && p.status !== "none" && (
              <>
                {" · "}v{p.version} ·{" "}
                <span className={p.status === "finalized" ? "text-emerald-600" : "text-slate-500"}>
                  {t(`paper.status.${p.status}`)}
                </span>
                {" · "}{t("paper.total", { total: p.total })}
              </>
            )}
          </p>
        </div>
        <div className="flex gap-2">
          {p?.status !== "none" && editable && (
            <button
              type="button"
              disabled={Math.abs((p?.total ?? 0) - exam.max_total) > 0.01 || finalize.isPending}
              onClick={() => finalize.mutate({})}
              className="px-3 py-1.5 text-sm font-medium rounded bg-emerald-500 text-white hover:bg-emerald-600 disabled:opacity-50"
            >
              {t("paper.finalize")}
            </button>
          )}
          {(p?.status === "finalized" || p?.status === "printed") && (
            <button
              type="button"
              disabled={unlock.isPending}
              onClick={() => {
                if (window.confirm(t("paper.unlockConfirm"))) unlock.mutate({});
              }}
              className="px-3 py-1.5 text-sm rounded border border-amber-300 text-amber-600 hover:bg-amber-50"
            >
              {t("paper.unlock")}
            </button>
          )}
          {(p?.status === "finalized" || p?.status === "printed") && (
            <Link
              to={`/exams/${examId}/scores`}
              className="px-3 py-1.5 text-sm font-medium rounded bg-sky-600 text-white hover:bg-sky-700"
            >
              {t("paper.toScoring")} →
            </Link>
          )}
        </div>
      </header>

      {!hasApprovedBp && (
        <p className="rounded border border-amber-200 bg-amber-50 p-3 text-sm text-amber-700">
          {t("paper.needBlueprint")}
        </p>
      )}

      {hasApprovedBp && (p?.status === "none" || editable) && (
        <div className="mb-4 flex items-center gap-2">
          <button
            type="button"
            disabled={busy}
            onClick={() => {
              if (p?.status !== "none" && !window.confirm(t("paper.regenConfirm"))) return;
              generate.mutate({ notes: instruction });
            }}
            className="px-4 py-2 text-sm font-medium rounded bg-violet-500 text-white hover:bg-violet-600 disabled:opacity-50"
          >
            {generate.isPending
              ? t("paper.generating")
              : p?.status === "none"
                ? t("paper.generate")
                : t("paper.regenerateAll")}
          </button>
          {generate.data && (
            <span className="text-xs text-slate-500">
              {t("paper.genStats", { reused: generate.data.reused ?? 0, generated: generate.data.generated ?? 0 })}
              {(generate.data.cell_failures?.length ?? 0) > 0 && (
                <span className="text-rose-500"> · {t("paper.cellFailures", { n: generate.data.cell_failures!.length })}</span>
              )}
            </span>
          )}
          {generate.isError && <span className="text-xs text-rose-500">{String(generate.error)}</span>}
        </div>
      )}

      {p && p.items.length > 0 && (
        <>
          {editable && (
            <div className="mb-4 rounded-lg border border-slate-200 bg-white p-3">
              <div className="flex flex-wrap items-center gap-2">
                <span className="text-xs text-slate-500">{t("paper.selected", { n: sel.size })}</span>
                <input
                  value={instruction}
                  onChange={(e) => setInstruction(e.target.value)}
                  placeholder={t("paper.instructionPlaceholder")}
                  className="flex-1 min-w-56 rounded border border-slate-200 px-2 py-1.5 text-sm"
                />
                <button
                  type="button"
                  disabled={sel.size === 0 || busy}
                  onClick={() =>
                    revise.mutate(
                      { regenerate_seqs: [...sel], remove_seqs: [], instruction },
                      { onSuccess: () => setSel(new Set()) },
                    )
                  }
                  className="px-3 py-1.5 text-sm rounded bg-violet-500 text-white hover:bg-violet-600 disabled:opacity-50"
                >
                  {revise.isPending ? t("paper.revising") : t("paper.regenerateSel")}
                </button>
                <button
                  type="button"
                  disabled={sel.size === 0 || busy}
                  onClick={() => {
                    if (window.confirm(t("paper.removeConfirm", { n: sel.size })))
                      revise.mutate(
                        { regenerate_seqs: [], remove_seqs: [...sel], instruction: "" },
                        { onSuccess: () => setSel(new Set()) },
                      );
                  }}
                  className="px-3 py-1.5 text-sm rounded border border-rose-200 text-rose-500 hover:bg-rose-50 disabled:opacity-50"
                >
                  {t("paper.removeSel")}
                </button>
                <label className="ml-auto flex items-center gap-1 text-xs text-slate-500">
                  <input type="checkbox" checked={showAnswers} onChange={(e) => setShowAnswers(e.target.checked)} />
                  {t("paper.showAnswers")}
                </label>
              </div>
              {revise.isError && <p className="mt-1 text-xs text-rose-500">{String(revise.error)}</p>}
            </div>
          )}

          <div className="space-y-2">
            {p.items.map((it) => (
              <div key={it.paper_item_id} className="rounded-lg border border-slate-200 bg-white p-3 text-sm">
                <div className="flex items-start gap-2">
                  {editable && (
                    <input type="checkbox" checked={sel.has(it.seq)} onChange={() => toggle(it.seq)} className="mt-1" />
                  )}
                  <div className="min-w-0 flex-1">
                    <div className="flex items-baseline gap-2">
                      <span className="font-semibold text-slate-700">{it.seq}.</span>
                      <span className="rounded bg-slate-50 border border-slate-200 px-1.5 text-[11px] text-slate-500">
                        {it.section_label || it.item_type}
                      </span>
                      <span className="text-[11px] text-slate-400">{it.score}{t("paper.pts")}</span>
                      {it.source === "teacher_upload" || it.source === "past_paper" ? (
                        <span className="text-[11px] text-sky-500">{t("paper.fromBank")}</span>
                      ) : (
                        <span className="text-[11px] text-violet-500">{t("paper.aiGenerated")}</span>
                      )}
                    </div>
                    <p className="mt-1 whitespace-pre-wrap text-slate-800">{renderStemMd(stripInlineOptions(it.stem, it.options))}</p>
                    {it.options && (
                      <div className="mt-1 grid grid-cols-2 gap-x-4 text-slate-600">
                        {it.options.map((o) => (
                          <span key={o.key}>{o.key}. <MathText text={o.text} /></span>
                        ))}
                      </div>
                    )}
                    {showAnswers && (
                      <div className="mt-1.5 rounded bg-emerald-50/60 border border-emerald-100 px-2 py-1 text-xs text-emerald-800 whitespace-pre-wrap">
                        {it.answer_key
                          ? <>{t("paper.answer")}: <MathText text={formatAnswer(it.answer_key)} /></>
                          : it.rubric
                            ? `${t("paper.rubric")}: ${it.rubric.map((r) => `${r.band} ${r.criteria}`).join(" | ")}`
                            : "—"}
                      </div>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>

          <HtmlPaperPanel examId={examId!} />

          {(p.status === "finalized" || p.status === "printed") && (
            <PrintoutsPanel examId={examId!} classId={(bp.data?.exam as { class_id?: string | null } | undefined)?.class_id ?? null} />
          )}

          {p.versions.length > 1 && (
            <div className="mt-4 rounded-lg border border-slate-200 bg-white p-3">
              <h3 className="text-xs font-semibold text-slate-500 mb-1.5">{t("paper.versions")}</h3>
              {p.versions.map((v) => (
                <div key={v.version} className="flex items-center gap-2 py-0.5 text-xs text-slate-500">
                  <span className="font-mono">v{v.version}</span>
                  <span className="flex-1 truncate">{v.change_summary}</span>
                  <span>{v.created_at.slice(0, 16).replace("T", " ")}</span>
                  {editable && v.version !== p.version && (
                    <button
                      type="button"
                      disabled={rollback.isPending}
                      onClick={() => rollback.mutate(v.version)}
                      className="rounded border border-slate-200 px-1.5 py-0.5 text-slate-500 hover:bg-slate-50"
                    >
                      {t("paper.rollback")}
                    </button>
                  )}
                </div>
              ))}
            </div>
          )}
        </>
      )}
    </div>
  );
}
